import { Badge } from '@amore/ui/components/badge'
import { Button } from '@amore/ui/components/button'
import { Card, CardHeader, CardTitle } from '@amore/ui/components/card'
import { Text } from '@amore/ui/components/typography'
import { Link } from '@tanstack/react-router'
import { ArrowRight, Heart, PencilLine, Users } from 'lucide-react'
import { useCanEdit } from '@/mock/store'

/** A preview of Skupinové fotky; the list itself lives on its own tab. */
export function PhotoGroupsCard({
  groups,
  slotMinutes,
}: {
  groups: { count: number; must: number; minutes: number } | null
  /** Length of "Skupinové focení" in Harmonogram, if it is there. */
  slotMinutes: number | null
}) {
  const canEdit = useCanEdit()
  const short = groups && slotMinutes !== null && slotMinutes < groups.minutes
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Users className="size-4 text-action" aria-hidden="true" />
          <CardTitle as="h2">Skupinové fotky</CardTitle>
        </div>
        {groups ? <Badge tone="neutral">{groups.count} skupin</Badge> : null}
      </div>
      {groups ? (
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <p className="text-2xl font-semibold tabular-nums">{groups.must}</p>
            <Text variant="small" tone="muted">
              musí být
            </Text>
          </div>
          <div>
            <p className="text-2xl font-semibold tabular-nums">~{groups.minutes} min</p>
            <Text
              variant="small"
              tone="muted"
              className={short ? 'font-semibold text-warning-foreground' : undefined}
            >
              {slotMinutes === null
                ? 'V harmonogramu na ně zatím není čas'
                : `V harmonogramu je ${slotMinutes} min`}
            </Text>
          </div>
        </div>
      ) : (
        <Text tone="muted" className="mt-3">
          Rodina, svědci, přátelé. Sepište, s kým chcete mít společnou fotku, ať na nikoho
          nezapomenete.
        </Text>
      )}
      <div className="mt-3 flex justify-end">
        <Link
          to="/photo/groups"
          className="inline-flex min-h-9 items-center gap-1.5 px-3 text-sm font-semibold text-action underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
        >
          {groups ? 'Zobrazit seznam' : canEdit ? 'Sestavit seznam' : 'Zobrazit'}
          <ArrowRight className="size-3.5" aria-hidden="true" />
        </Link>
      </div>
    </Card>
  )
}

/** What the couple wishes for, in their own words; edited in its sheet, sent with the brief. */
export function PhotoWishes({
  wishes,
  onPreview,
}: {
  wishes: string
  onPreview: (title: string, description?: string) => void
}) {
  const canEdit = useCanEdit()
  return (
    <Card padding="none" className="overflow-hidden">
      <CardHeader appearance="strip">
        <div className="flex items-center gap-2.5">
          <Heart className="size-4 text-action" aria-hidden="true" />
          <CardTitle as="h2">Přání pro fotografa</CardTitle>
        </div>
        {canEdit ? (
          <Button
            appearance="ghost"
            tone="neutral"
            size="sm"
            aria-label="Upravit přání pro fotografa"
            onClick={() => onPreview('Úpravu přání připravujeme')}
          >
            <PencilLine className="size-3.5" aria-hidden="true" />
            Upravit
          </Button>
        ) : null}
      </CardHeader>
      <div className="space-y-2 p-5">
        {wishes ? (
          <p className="text-sm whitespace-pre-line">{wishes}</p>
        ) : (
          <Text tone="muted">
            {canEdit
              ? 'Styl, na čem vám záleží, koho nevynechat, co raději nefotit. Fotograf si podle toho připraví, co a kdy nafotí.'
              : 'Zatím bez přání.'}
          </Text>
        )}
        {wishes ? (
          <Text variant="small" tone="muted">
            Přání půjdou fotografovi v podkladech.
          </Text>
        ) : null}
      </div>
    </Card>
  )
}
